import {
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { BaseLogo } from "../../core/models/entities/BaseLogo";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import { PageQuery, toQueryParam } from "../../utils/models/pageQuery";
import { Organization } from "../../core/models/entities/Organization";
import { PagedResponse } from "../models/responses/pagedResponse";
import { mapToLogo } from "../models/mappers/mapLogo";
import { LogoOptions } from "../../core/models/entities/LogoOptions";
import { mapLogoOptions } from "../models/mappers/mapLogoOptions";
import { TransferOptions } from "../../core/models/entities/TransferOptions";
import { mapTransferOptions } from "../models/mappers/mapTransferOptions";
import {
  ChangeLogoColors,
  InitiateApproval,
} from "../../presentation/screens/logo-list/hooks/useLogoActionsHandler";
import { LogoCommit } from "../models/bodies/LogoCommit";
import { CreateMaskQueueCount } from "../models/responses/CreateMaskQueueCount";
import { Verdict } from "../../presentation/components/ApprovalVoting/approval-voting";
import { LogoStorageCountByOrgDto } from "../models/responses/LogoDto";

export interface LogoRepository {
  changeColors(
    orgId: Organization["id"],
    logoId: BaseLogo["id"],
    colors: ChangeLogoColors,
  ): Promise<BaseLogo>;
  changeTitle(
    orgId: Organization["id"],
    logoId: BaseLogo["id"],
    title: string,
  ): Promise<BaseLogo>;
  useLogo(orgId: number, logoId: number): Resource<BaseLogo>;
  useOptions(): Resource<LogoOptions>;
  useTransferOptions(vendorId?: number): Resource<TransferOptions>;
  useAllLogos(currentPageQuery?: PageQuery): Resource<PagedResponse<BaseLogo>>;
  prefetchLogos(query: PageQuery): void;
  useAllLogosForDesignInfinity(
    customerId: number,
    currentPageQuery?: PageQuery,
  ): UseInfiniteQueryResult<PagedResponse<BaseLogo>>;
  useAllLogosInfinity(
    query: PageQuery,
  ): UseInfiniteQueryResult<PagedResponse<BaseLogo>>;
  createLogo(logo: File, orgId: number): Promise<BaseLogo>;
  createTextLogo(
    value: string,
    color: string,
    heightMm: number,
    font: string,
    orgId: number,
    customerId?: number,
    method?: string,
  ): Promise<BaseLogo>;
  createNameLogo(
    color: string,
    heightMm: number,
    font: string,
    orgId: number,
    customerId?: number,
    method?: string,
  ): Promise<BaseLogo>;
  createNumberLogo(
    color: string,
    heightMm: number,
    font: string,
    method: string,
    orgId: number,
    customerId?: number,
  ): Promise<BaseLogo>;
  createBaseLogo(customerId: number, title: string): Promise<BaseLogo>;
  uploadFileToBaseLogo(
    logo: File,
    customerId: number,
    logoId: number,
  ): Promise<BaseLogo>;
  update(orgId: number, logo: BaseLogo): Promise<BaseLogo>;
  resize(orgId: number, logo: BaseLogo): Promise<BaseLogo>;
  copy(orgId: number, logoId: number): Promise<BaseLogo>;
  commit(logo: BaseLogo, commit: LogoCommit): Promise<BaseLogo>;
  initiateApproval(
    logo: BaseLogo,
    approval: InitiateApproval,
  ): Promise<BaseLogo>;
  sendApproval(logo: BaseLogo, email: string): Promise<void>;
  sendApprovalConditional(logo: BaseLogo, email: string): Promise<void>;
  placeVerdict(logo: BaseLogo, verdict: Verdict): Promise<BaseLogo>;
  delete(orgId: number, logoId: number): Promise<void>;
  generateTextLogoPreview: (
    value: string,
    color: string,
    heightMM: number,
    font: string,
    orgId: number,
  ) => Promise<string>;
  generateNumberLogoPreview: (
    color: string,
    heightMm: number,
    font: string,
    orgId: number,
  ) => Promise<string>;
  refreshStorageCount(logoId: BaseLogo["id"]): Promise<void>;
  getLogoStorageCounts(
    logoId: BaseLogo["id"],
  ): Promise<LogoStorageCountByOrgDto[]>;
  refreshLogoStorageCountForOrg(
    logoId: BaseLogo["id"],
    organizationId: number,
  ): Promise<void>;
  getImportLogosQueueCounts(): Promise<CreateMaskQueueCount[]>;
  useImportLogosQueueCounts(): Resource<CreateMaskQueueCount[]>;
  getImportPricesQueueCounts(): Promise<CreateMaskQueueCount[]>;
}

export function useLogoRepository(): LogoRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  async function invalidateQueries() {
    await queryClient.invalidateQueries({
      queryKey: ["logos"],
      type: "all",
    });
  }

  function useOptions(): Resource<LogoOptions> {
    return useQuery(
      ["logo-options"],
      () => apiClient.getLogoOptions().then(mapLogoOptions),
      {
        staleTime: 600000,
      },
    );
  }

  function useTransferOptions(vendorId?: number): Resource<TransferOptions> {
    return useQuery(["transfer-options"], () =>
      apiClient.getTransferOptions(vendorId).then(mapTransferOptions),
    );
  }

  function useLogo(orgId: number, logoId: number): Resource<BaseLogo> {
    return useQuery(["logos", logoId], () =>
      apiClient.getLogo(orgId, logoId).then(mapToLogo),
    );
  }

  function useAllLogos(query: PageQuery): Resource<PagedResponse<BaseLogo>> {
    // If a logo type was filtered, extend the route by a type parameter.
    const typeFilter = query.filterBy.find((it) => it.field === "logoType");
    const queryCopy = { ...query };
    queryCopy.filterBy = queryCopy.filterBy.filter(
      (it) => it.field !== "logoType",
    );
    return useQuery(
      ["logos", typeFilter?.value, toQueryParam(query)],
      () =>
        apiClient
          .getAllLogos(queryCopy, typeFilter?.value)
          .then((data) => ({ ...data, results: data.results.map(mapToLogo) })),
      { staleTime: 60000, refetchOnWindowFocus: false }, // 1 min
    );
  }

  // Prefetch a page into the same cache useAllLogos reads. Does NOT change the
  // main query behaviour (no keepPreviousData) so the page state-machine is intact,
  // and it respects the same 60s staleTime so prices stay as fresh as a normal click.
  function prefetchLogos(query: PageQuery): void {
    const typeFilter = query.filterBy.find((it) => it.field === "logoType");
    const queryCopy = { ...query };
    queryCopy.filterBy = queryCopy.filterBy.filter(
      (it) => it.field !== "logoType",
    );
    queryClient.prefetchQuery(
      ["logos", typeFilter?.value, toQueryParam(query)],
      () =>
        apiClient
          .getAllLogos(queryCopy, typeFilter?.value)
          .then((data) => ({ ...data, results: data.results.map(mapToLogo) })),
      { staleTime: 60000 },
    );
  }

  function useAllLogosForDesignInfinity(
    customerId: number,
    query: PageQuery,
  ): UseInfiniteQueryResult<PagedResponse<BaseLogo>> {
    return useInfiniteQuery({
      queryKey: ["logos", customerId, "inf", toQueryParam(query)],
      queryFn: ({ pageParam }) => {
        const copy: PageQuery = {
          page: {
            index: pageParam ?? query.page.index,
            size: query.page.size,
          },
          filterBy: query.filterBy,
          sortBy: {
            field: query.sortBy.field,
            sortDirection: query.sortBy.sortDirection,
          },
          searchTerm: query.searchTerm,
        };

        return apiClient.getAllLogosForDesign(customerId, copy);
      },
      getNextPageParam: (lastPage) => {
        if (lastPage.pageIndex === lastPage.pageCount) {
          return undefined;
        }
        return lastPage.pageIndex + 1;
      },
      getPreviousPageParam: (firstPage) => {
        if (firstPage.pageIndex === 1) {
          return undefined;
        }

        return firstPage.pageIndex - 1;
      },
    });
  }

  function useAllLogosInfinity(
    query: PageQuery,
  ): UseInfiniteQueryResult<PagedResponse<BaseLogo>> {
    return useInfiniteQuery({
      queryKey: ["logos", "inf", toQueryParam(query)],
      queryFn: ({ pageParam }) => {
        const copy: PageQuery = {
          page: {
            index: pageParam ?? query.page.index,
            size: query.page.size,
          },
          filterBy: query.filterBy,
          sortBy: {
            field: query.sortBy.field,
            sortDirection: query.sortBy.sortDirection,
          },
          searchTerm: query.searchTerm,
        };

        return apiClient.getAllLogos(copy);
      },
      getNextPageParam: (lastPage) => {
        if (lastPage.pageIndex === lastPage.pageCount) {
          return undefined;
        }
        return lastPage.pageIndex + 1;
      },
      getPreviousPageParam: (firstPage) => {
        if (firstPage.pageIndex === 1) {
          return undefined;
        }

        return firstPage.pageIndex - 1;
      },
    });
  }

  function deleteLogo(orgId: number, logoId: number): Promise<void> {
    // Optimistically remove from cache immediately
    queryClient.setQueriesData<PagedResponse<BaseLogo>>(
      ["logos"],
      (oldData) => {
        if (!oldData || !oldData.results) return oldData;

        return {
          ...oldData,
          results: oldData.results.filter((logo) => logo.id !== logoId),
        };
      },
    );

    // Remove individual logo cache entry
    queryClient.removeQueries({ queryKey: ["logos", logoId] });

    // Update infinite queries - use predicate function to match infinite queries
    queryClient.setQueriesData(["logos"], (oldData: any) => {
      if (!oldData || !oldData.pages) return oldData;

      return {
        ...oldData,
        pages: oldData.pages.map((page: PagedResponse<BaseLogo>) => ({
          ...page,
          results: page.results
            ? page.results.filter((logo: BaseLogo) => logo.id !== logoId)
            : [],
        })),
      };
    });

    return apiClient.deleteLogoById(orgId, logoId).catch((error) => {
      // If deletion fails, invalidate queries to restore correct state
      invalidateQueries();
      throw error;
    });
  }

  async function createImageLogo(
    logoFile: File,
    orgId: number,
  ): Promise<BaseLogo> {
    let lr = await apiClient.createAndUploadImageLogo(logoFile, orgId);
    await invalidateQueries();
    return mapToLogo(lr);
  }

  async function createTextLogo(
    value: string,
    color: string,
    heightMM: number,
    font: string,
    orgId: number,
    customerId?: number,
    method?: string,
  ): Promise<BaseLogo> {
    let lr = await apiClient.createTextLogo(orgId, {
      color: color,
      heightMm: heightMM,
      value: value,
      font: font,
      customerId: customerId,
      method: method,
    });
    await invalidateQueries();
    return mapToLogo(lr);
  }

  async function createNameLogo(
    color: string,
    heightMM: number,
    font: string,
    orgId: number,
    customerId?: number,
    method?: string,
  ): Promise<BaseLogo> {
    let lr = await apiClient.createPlaceholderNameLogo(orgId, {
      color: color,
      heightMm: heightMM,
      font: font,
      method: method,
      customerId: customerId,
    });
    await invalidateQueries();
    return mapToLogo(lr);
  }

  async function createNumberLogo(
    color: string,
    heightMM: number,
    font: string,
    method: string,
    orgId: number,
    customerId?: number,
  ): Promise<BaseLogo> {
    let lr = await apiClient.createPlaceholderNumberLogo(orgId, {
      color: color,
      heightMm: heightMM,
      font: font,
      method: method,
      customerId: customerId,
    });
    await invalidateQueries();
    return mapToLogo(lr);
  }

  async function createBaseLogo(
    organizationId: number,
    title: string,
  ): Promise<BaseLogo> {
    // TODO Fix own logged in user's id.
    let lr = await apiClient.createImageLogo({
      title,
      customerId: organizationId,
    });
    return mapToLogo(lr);
  }

  async function uploadFileToBaseLogo(
    logo: File,
    customerId: number,
    logoId: number,
  ): Promise<BaseLogo> {
    let lr = await apiClient.uploadFileImageLogo(logo, customerId, logoId);
    return mapToLogo(lr);
  }

  async function changeColors(
    orgId: Organization["id"],
    logoId: BaseLogo["id"],
    colors: ChangeLogoColors,
  ): Promise<BaseLogo> {
    // console.log("changeColors", colors);

    const lrDto = await apiClient.changeColors(orgId, logoId, colors);
    const lr = mapToLogo(lrDto as any);

    queryClient.setQueryData(["logos", logoId], lr);
    queryClient.setQueriesData<PagedResponse<BaseLogo>>(
      ["logos"],
      (oldData) => {
        if (!oldData || !oldData.results) return oldData;
        return {
          ...oldData,
          results: oldData.results.map((item) =>
            item.id === logoId ? lr : item,
          ),
        };
      },
    );

    queryClient.setQueriesData(["logos"], (oldData: any) => {
      if (!oldData || !oldData.pages) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page: PagedResponse<BaseLogo>) => ({
          ...page,
          results: page.results
            ? page.results.map((item: BaseLogo) =>
                item.id === logoId ? lr : item,
              )
            : [],
        })),
      };
    });

    return lr;
  }

  async function changeTitle(
    orgId: Organization["id"],
    logoId: number,
    title: string,
  ): Promise<BaseLogo> {
    const lr = await apiClient.changeLogoTitle(orgId, logoId, title);
    await invalidateQueries();
    return lr;
  }

  async function update(orgId: number, logo: BaseLogo): Promise<BaseLogo> {
    const lr = await apiClient.updateLogo(orgId, logo);
    return lr;
  }

  async function resize(orgId: number, logo: BaseLogo): Promise<BaseLogo> {
    const lr = await apiClient.resizeLogo(orgId, logo);
    await invalidateQueries();
    return lr;
  }

  async function copy(orgId: number, logoId: number): Promise<BaseLogo> {
    const lr = await apiClient.copyLogo(orgId, logoId);
    await invalidateQueries();
    return lr;
  }

  async function commit(
    logo: BaseLogo,
    logoCommit: LogoCommit,
  ): Promise<BaseLogo> {
    const lr = await apiClient.commitLogo(logo, logoCommit);
    await invalidateQueries();
    return lr;
  }

  async function placeVerdict(
    logo: BaseLogo,
    verdict: Verdict,
  ): Promise<BaseLogo> {
    const lrDto = await apiClient.placeVerdictLogo(logo, verdict);
    const lr = mapToLogo(lrDto as any);
    queryClient.setQueryData(["logos", lr.id], lr);
    queryClient.setQueriesData<PagedResponse<BaseLogo>>(
      ["logos"],
      (oldData) => {
        if (!oldData || !oldData.results) return oldData;
        return {
          ...oldData,
          results: oldData.results.map((item) =>
            item.id === lr.id ? lr : item,
          ),
        };
      },
    );
    queryClient.setQueriesData(["logos"], (oldData: any) => {
      if (!oldData || !oldData.pages) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page: PagedResponse<BaseLogo>) => ({
          ...page,
          results: page.results
            ? page.results.map((item: BaseLogo) =>
                item.id === lr.id ? lr : item,
              )
            : [],
        })),
      };
    });
    return lr;
  }

  async function initiateApproval(
    logo: BaseLogo,
    approval: InitiateApproval,
  ): Promise<BaseLogo> {
    const lrDto = await apiClient.initiateApprovalLogo(logo, approval);
    const lr = mapToLogo(lrDto as any);

    queryClient.setQueryData(["logos", lr.id], lr);
    await queryClient.invalidateQueries({
      queryKey: ["logos"],
      predicate: (query) => {
        const key = query.queryKey;
        // Invalidate all 'logos' queries EXCEPT the specific detail query for a logo ID
        // Detail query format: ["logos", number]
        // List query format: ["logos", string, string] or ["logos", number, "inf", string]
        return !(key.length === 2 && typeof key[1] === "number");
      },
    });

    return lr;
  }

  async function sendApproval(logo: BaseLogo, email: string): Promise<void> {
    const lr = await apiClient.sendApprovalLogo(logo, email);
  }

  async function sendApprovalConditional(
    logo: BaseLogo,
    email: string,
  ): Promise<void> {
    const lr = await apiClient.sendApprovalLogoConditional(logo, email);
  }

  async function generateTextLogoPreview(
    value: string,
    color: string,
    heightMM: number,
    font: string,
    orgId: number,
  ): Promise<string> {
    return await apiClient.generateTextLogoPreview(orgId, {
      color: color,
      heightMm: heightMM,
      value: value,
      font: font,
    });
  }

  async function generateNumberLogoPreview(
    color: string,
    heightMm: number,
    font: string,
    orgId: number,
  ): Promise<string> {
    return await apiClient.generateNumberLogoPreview(orgId, {
      color: color,
      heightMm: heightMm,
      font: font,
    });
  }

  async function refreshStorageCount(logoId: BaseLogo["id"]): Promise<void> {
    await apiClient.refreshStorageCount(logoId);
    await invalidateQueries();
  }

  async function getLogoStorageCounts(
    logoId: BaseLogo["id"],
  ): Promise<LogoStorageCountByOrgDto[]> {
    return await apiClient.getLogoStorageCounts(logoId);
  }

  async function refreshLogoStorageCountForOrg(
    logoId: BaseLogo["id"],
    organizationId: number,
  ): Promise<void> {
    await apiClient.refreshLogoStorageCountForOrg(logoId, organizationId);
    await invalidateQueries();
  }

  async function getImportLogosQueueCounts(): Promise<CreateMaskQueueCount[]> {
    return await apiClient.getImportLogosQueueCounts();
  }

  // React Query version for the header queue badges: dedups the header's repeated
  // remounts into one request (the imperative call used to fire ~5x per page load)
  // while keeping the 60s polling via refetchInterval.
  function useImportLogosQueueCounts(): Resource<CreateMaskQueueCount[]> {
    return useQuery(
      ["import-queue-counts", "logos"],
      () => apiClient.getImportLogosQueueCounts(),
      {
        refetchInterval: 60000,
        staleTime: 55000,
      },
    );
  }

  async function getImportPricesQueueCounts(): Promise<CreateMaskQueueCount[]> {
    return await apiClient.getImportPricesQueueCounts();
  }

  return {
    useOptions,
    useTransferOptions,
    useLogo,
    useAllLogos,
    prefetchLogos,
    useAllLogosForDesignInfinity,
    delete: deleteLogo,
    createLogo: createImageLogo,
    createTextLogo,
    createNameLogo,
    createNumberLogo,
    createBaseLogo,
    uploadFileToBaseLogo,
    changeColors,
    changeTitle,
    update,
    resize,
    copy,
    commit,
    useAllLogosInfinity,
    initiateApproval,
    sendApproval,
    sendApprovalConditional,
    placeVerdict,
    generateTextLogoPreview,
    generateNumberLogoPreview,
    refreshStorageCount,
    getLogoStorageCounts,
    refreshLogoStorageCountForOrg,
    getImportLogosQueueCounts,
    useImportLogosQueueCounts,
    getImportPricesQueueCounts,
  };
}
